import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  selectedApplication: null,
  loading: false,
  error: null
};

// Fetch all job applications
export const fetchJobApplications = createAsyncThunk(
  'jobApplications/fetchJobApplications',
  async (queryParams, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();

      const response = await api.get(`${api_url_v1}/job-applications`, {
        params: queryParams,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return {
        data: response.data.data || [],
        meta: response.data.meta || {
          current_page: queryParams.page,
          last_page: 1,
          per_page: queryParams.limit,
          total: 0
        }
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch job applications');
    }
  }
);




// Fetch single job application
export const fetchJobApplicationDetails = createAsyncThunk(
  'jobApplications/fetchJobApplicationDetails',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.get(`${api_url_v1}/job-applications/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch job application details');
    }
  }
);

// Update job application
export const updateJobApplication = createAsyncThunk(
  'jobApplications/updateJobApplication',
  async ({ id, data }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.put(`${api_url_v1}/job-applications/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update job application');
    }
  }
);

// Helper function to normalize application data
const normalizeApplication = (application) => ({
  ...application,
  job_posting: {
    ...application.job_posting,
    application_deadline: new Date(application.job_posting.application_deadline)
  },
  created_at: new Date(application.created_at),
  updated_at: new Date(application.updated_at)
});

const jobApplicationsSlice = createSlice({
  name: 'jobApplications',
  initialState,
  reducers: {
    setSelectedApplication: (state, action) => {
      state.selectedApplication = action.payload;
    },
    clearSelectedApplication: (state) => {
      state.selectedApplication = null;
    },
    updateApplicationStatus: (state, action) => {
      const { id, status } = action.payload;
      const application = state.items.find(app => app.id === id);
      if (application) {
        application.status = status;
      }
      if (state.selectedApplication?.id === id) {
        state.selectedApplication.status = status;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch job applications cases
      .addCase(fetchJobApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobApplications.fulfilled, (state, action) => {
        console.log(action.payload)
        state.loading = false;
        state.items = action.payload.data.map(normalizeApplication);
      })
      .addCase(fetchJobApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

     

      // Fetch job application details cases
      .addCase(fetchJobApplicationDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobApplicationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedApplication = normalizeApplication(action.payload);
      })
      .addCase(fetchJobApplicationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update job application cases
      .addCase(updateJobApplication.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJobApplication.fulfilled, (state, action) => {
        state.loading = false;
        const normalized = normalizeApplication(action.payload);
        const index = state.items.findIndex(app => app.id === normalized.id);
        if (index !== -1) {
          state.items[index] = normalized;
        }
        if (state.selectedApplication?.id === normalized.id) {
          state.selectedApplication = normalized;
        }
      })
      .addCase(updateJobApplication.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setSelectedApplication,
  clearSelectedApplication,
  updateApplicationStatus
} = jobApplicationsSlice.actions;

export default jobApplicationsSlice.reducer;